/**
 * DD Assistant Strategy Pattern Types
 *
 * Enables switching between MVP (prompt-based) and RAG implementations
 * via feature flag or configuration.
 */

import type { DDChatMessage, DDStartupContext } from '@/lib/supabase/edge-functions';

// ============================================
// Core Types
// ============================================

/** Mode identifier for tracking which implementation generated response */
export type DDAssistantMode = 'mvp' | 'rag';

/** Source attribution for RAG responses */
export interface DDSource {
  type: 'document' | 'history' | 'profile' | 'score';
  label: string;
  snippet?: string;
  /** Document ID or conversation ID for linking */
  referenceId?: string;
  /** Page number or chunk index */
  location?: string;
  /** Relevance score from vector search (0-1) */
  relevance?: number;
}

/** Context passed to assistant for each request */
export interface DDAssistantContext {
  /** The user's question or message */
  message: string;
  /** Startup being analyzed */
  startupId: string;
  /** Investor making the request */
  investorId: string;
  /** Full startup context (profile, scores, etc.) */
  startupContext: DDStartupContext;
  /** Previous messages in current session */
  history?: DDChatMessage[];
  /** Optional conversation ID for memory retrieval */
  conversationId?: string;
  /** Analysis focus area */
  focus?: DDAnalysisFocus;
}

/** Response from assistant */
export interface DDAssistantResponse {
  /** The assistant's response message */
  message: DDChatMessage;
  /** Sources used to generate response (RAG only) */
  sources?: DDSource[];
  /** Suggested follow-up questions */
  suggestedQuestions: string[];
  /** Topics covered in conversation */
  topicsDiscussed: string[];
  /** Token usage for cost tracking */
  usage: {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    /** Tokens from retrieved context (RAG only) */
    retrievalTokens?: number;
  };
  /** Which implementation generated this response */
  mode: DDAssistantMode;
  /** Processing time in ms */
  processingTimeMs: number;
}

/** Analysis focus areas */
export type DDAnalysisFocus =
  | 'team'
  | 'market'
  | 'product'
  | 'traction'
  | 'financials'
  | 'risks'
  | 'competition'
  | 'exit'
  | 'general';

// ============================================
// Assistant Interface
// ============================================

/**
 * Core interface for DD Assistant implementations.
 *
 * Both PromptBasedAssistant (MVP) and RAGAssistant implement this interface,
 * allowing runtime switching via factory pattern.
 */
export interface IDDAssistant {
  /**
   * Send a message and get a response.
   * This is the main interaction method.
   */
  chat(context: DDAssistantContext): Promise<DDAssistantResponse>;

  /**
   * Get conversation history for a startup-investor pair.
   * Only available in RAG mode (returns empty array in MVP).
   */
  getHistory(investorId: string, startupId: string): Promise<DDConversationSummary[]>;

  /**
   * Clear conversation history.
   * MVP: clears session state
   * RAG: optionally deletes from storage
   */
  clearHistory(investorId: string, startupId: string, permanent?: boolean): Promise<void>;

  /**
   * Get the current mode identifier.
   */
  getMode(): DDAssistantMode;
}

// ============================================
// Conversation History Types (for RAG)
// ============================================

/** Summary of a past conversation */
export interface DDConversationSummary {
  conversationId: string;
  startupId: string;
  startupName: string;
  investorId: string;
  /** Auto-generated summary of discussion */
  summary: string;
  /** Main topics covered */
  topics: string[];
  /** Number of messages exchanged */
  messageCount: number;
  /** Total tokens used */
  totalTokens: number;
  createdAt: string;
  lastMessageAt: string;
}

/** Full conversation with messages */
export interface DDConversation extends DDConversationSummary {
  messages: DDChatMessage[];
}

// ============================================
// Configuration Types
// ============================================

/** Configuration for creating DD assistant */
export interface DDAssistantConfig {
  /** Which implementation to use */
  mode: DDAssistantMode;
  /** Override for testing */
  forceMode?: DDAssistantMode;
  /** RAG-specific settings */
  ragConfig?: RAGConfig;
}

/** RAG implementation settings */
export interface RAGConfig {
  /** Max document chunks to retrieve */
  maxDocumentChunks: number;
  /** Max history items to retrieve */
  maxHistoryItems: number;
  /** Minimum relevance score for retrieval (0-1) */
  relevanceThreshold: number;
  /** Include citations in response */
  includeCitations: boolean;
  /** Summarize old conversations vs include verbatim */
  summarizeHistory: boolean;
}

/** Default RAG configuration */
export const DEFAULT_RAG_CONFIG: RAGConfig = {
  maxDocumentChunks: 8,
  maxHistoryItems: 5,
  relevanceThreshold: 0.7,
  includeCitations: true,
  summarizeHistory: true,
};

// ============================================
// Feature Flag Types
// ============================================

/** Feature flag configuration */
export interface DDFeatureFlags {
  /** Global DD mode */
  ddAssistantMode: DDAssistantMode;
  /** Enable RAG for specific tiers only */
  ragEnabledTiers: string[];
  /** Percentage of users to route to RAG (A/B test) */
  ragRolloutPercentage: number;
  /** Force specific users to RAG (for testing) */
  ragBetaUsers: string[];
}

/**
 * Default feature flags.
 *
 * `ddAssistantMode` DEFAULTS TO RAG, and that default is load-bearing — it is
 * not a preference.
 *
 * `EXPO_PUBLIC_*` variables are inlined at BUNDLE time. `eas.json`'s
 * `build.<profile>.env` therefore applies to BUILDS only: no workflow passes
 * `--environment` to `eas update`, so an OTA bundle carries none of it. Since
 * most releases here reach users as an OTA rather than a new binary, a default
 * of 'mvp' meant the DD assistant silently degraded on the next OTA even after
 * a correctly-configured binary shipped.
 *
 * That is exactly how RAG came to be built, deployed and never reached: it was
 * enabled in a local `.env` and nowhere that ships. Depending on build-time env
 * for a core product behaviour is the bug; the default is now the intended
 * behaviour, and the env var remains available to force 'mvp' deliberately.
 *
 * `ragEnabledTiers` / `ragRolloutPercentage` are only consulted by
 * `resolveAssistantMode()` (the per-user A/B path). They do NOT gate the
 * default above, so leaving the rollout at 0 does not switch RAG off.
 */
export const DEFAULT_DD_FEATURE_FLAGS: DDFeatureFlags = {
  ddAssistantMode: 'rag',
  ragEnabledTiers: ['premium'],
  ragRolloutPercentage: 0,
  ragBetaUsers: [],
};

/** Get mode based on feature flags and user context */
export function resolveAssistantMode(
  flags: DDFeatureFlags,
  userId: string,
  userTier: string
): DDAssistantMode {
  // Beta users always get RAG
  if (flags.ragBetaUsers.includes(userId)) {
    return 'rag';
  }

  // Check tier eligibility
  if (flags.ragEnabledTiers.length > 0 && !flags.ragEnabledTiers.includes(userTier)) {
    return 'mvp';
  }

  // A/B test rollout
  if (flags.ragRolloutPercentage < 100) {
    const hash = simpleHash(userId);
    if (hash % 100 >= flags.ragRolloutPercentage) {
      return 'mvp';
    }
  }

  return flags.ddAssistantMode;
}

/** Simple hash for consistent user bucketing */
function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}
